import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMissedIncomeUsers, getMissedIncomeTotals } from '../../api/adminApi';

const cyan = '#00e5ff';
const amber = '#ffaa44';
const red = '#ff6666';
const blue = '#66aaff';
const purple = '#cc66ff';

const TotalChip = ({ label, value, color }) => (
  <div style={{ background: `${color}12`, border: `1px solid ${color}35`, borderRadius: 10, padding: '10px 18px', display: 'flex', flexDirection: 'column', gap: 3 }}>
    <span style={{ color: '#666', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
    <span style={{ color, fontSize: 18, fontWeight: 800, fontFamily: 'monospace' }}>${value.toFixed(2)}</span>
  </div>
);

const AdminMissedIncome = () => {
  const [users, setUsers]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [pages, setPages]       = useState(1);
  const [page, setPage]         = useState(1);
  const [inputVal, setInputVal] = useState('');
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [totals, setTotals]         = useState(null);
  const [totalsLoading, setTotalsLoading] = useState(false);
  const [totalsError, setTotalsError]     = useState('');
  const navigate = useNavigate();

  const calculateTotals = () => {
    setTotalsLoading(true);
    setTotalsError('');
    getMissedIncomeTotals({ search })
      .then((res) => setTotals(res.data))
      .catch((err) => setTotalsError(err.response?.data?.message || 'Failed to calculate totals'))
      .finally(() => setTotalsLoading(false));
  };

  const load = useCallback(() => {
    setLoading(true);
    getMissedIncomeUsers({ search, page, limit: 15 })
      .then((res) => {
        setUsers(res.data.users || []);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 1);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); setSearch(inputVal); setTotals(null); };

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Missed Income</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 16 }}>
        {total} users — click a row to view detailed missed income breakdown
      </div>

      {/* Grand totals — computed on demand, across all users matching the current search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <button
          onClick={calculateTotals}
          disabled={totalsLoading}
          style={{ background: totalsLoading ? 'rgba(255,255,255,0.06)' : 'rgba(255,170,68,0.15)', border: `1px solid ${totalsLoading ? 'rgba(255,255,255,0.1)' : 'rgba(255,170,68,0.4)'}`, color: totalsLoading ? '#555' : amber, borderRadius: 8, padding: '9px 16px', fontWeight: 700, fontSize: 13, cursor: totalsLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
        >
          {totalsLoading ? 'Calculating…' : totals ? '↻ Recalculate Total' : '⚡ Calculate Total Missed Income'}
        </button>

        {totalsError && <span style={{ color: red, fontSize: 12 }}>{totalsError}</span>}

        {totals && !totalsLoading && (
          <>
            <TotalChip label="Total Missed" value={totals.totals.grandTotal} color={red} />
            <TotalChip label="Generation" value={totals.totals.generationTotal} color={blue} />
            <TotalChip label="Fastrack Held" value={totals.totals.fastrackTotal} color={purple} />
            <TotalChip label="Milestone" value={totals.totals.milestoneTotal} color={amber} />
            <span style={{ color: '#555', fontSize: 11 }}>across {totals.userCount} user{totals.userCount !== 1 ? 's' : ''}{search ? ' (matching search)' : ''}</span>
          </>
        )}
      </div>

      {/* Info banner */}
      <div style={{ background: 'rgba(255,170,68,0.08)', border: '1px solid rgba(255,170,68,0.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 12, color: amber }}>
        ⚡ Missed income is computed on-demand per user. Click any user to run the analysis — includes Generation Bonus gaps, Held Fastrack bonuses, Milestone penalties, and Performance rank gaps.
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <input
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          placeholder="Search by name, username, email or code..."
          style={{ flex: 1, background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '9px 14px', color: '#fff', fontSize: 13, outline: 'none' }}
        />
        <button type="submit" style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Search
        </button>
        {search && (
          <button type="button" onClick={() => { setInputVal(''); setSearch(''); setPage(1); setTotals(null); }}
            style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#888', borderRadius: 8, padding: '9px 14px', fontSize: 13, cursor: 'pointer' }}>
            Clear
          </button>
        )}
      </form>

      {/* Table */}
      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
          <thead>
            <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.12)' }}>
              {['#', 'Name', 'Active Slot', 'Total Missed', 'Generation', 'Fastrack Held', 'Milestone', ''].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center', padding: 40, fontSize: 13 }}>Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center', padding: 40, fontSize: 13 }}>No users found.</td></tr>
            ) : users.map((u, i) => {
              const s = u.missedIncomeSummary || { grandTotal: 0, generationTotal: 0, fastrackTotal: 0, milestoneTotal: 0 };
              return (
                <tr
                  key={u._id}
                  onClick={() => navigate(`/panel/missed-income/${u._id}`)}
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,170,68,0.04)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ color: '#555', fontSize: 12, padding: '11px 14px' }}>{(page - 1) * 15 + i + 1}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>{u.name}</div>
                    <div style={{ color: '#555', fontSize: 11 }}>
                      {u.username ? `@${u.username}` : '—'}&nbsp;
                      <span style={{ color: cyan, fontFamily: 'monospace' }}>{u.userCode}</span>
                    </div>
                  </td>
                  <td style={{ color: '#66ff99', fontSize: 12, padding: '11px 14px' }}>{u.planId?.planName || <span style={{ color: '#444' }}>None</span>}</td>
                  <td style={{ color: red, fontSize: 13, fontWeight: 700, padding: '11px 14px', fontFamily: 'monospace' }}>${s.grandTotal.toFixed(2)}</td>
                  <td style={{ color: blue, fontSize: 12, padding: '11px 14px', fontFamily: 'monospace' }}>${s.generationTotal.toFixed(2)}</td>
                  <td style={{ color: purple, fontSize: 12, padding: '11px 14px', fontFamily: 'monospace' }}>${s.fastrackTotal.toFixed(2)}</td>
                  <td style={{ color: amber, fontSize: 12, padding: '11px 14px', fontFamily: 'monospace' }}>${s.milestoneTotal.toFixed(2)}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{ background: 'rgba(255,170,68,0.12)', border: '1px solid rgba(255,170,68,0.3)', color: amber, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      View Analysis →
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
          {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)}
              style={{ background: p === page ? cyan : 'rgba(255,255,255,0.06)', border: 'none', color: p === page ? '#000' : '#888', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMissedIncome;
